/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { ItemTypeConsts } from 'client/util/Constants';
import { clone, mixin } from 'lodash';
import { parallelLimit } from 'async';
import ActiveDirectoryApi from 'client/api/v2/ActiveDirectoryApi';
import CollectionModule from 'client/store/modules/CollectionModule';
import ConfigurationGroupApi from 'api/v2/ConfigurationGroupApi';
import GroupApi from 'client/api/v2/GroupApi';
import ProgressMixin from 'client/store/mixins/ProgressMixin';
import SystemGroupApi from 'api/v2/SystemGroupApi';
import UserGroupApi from 'api/v2/UserGroupApi';
import Vue from 'vue';

const ApiMap = {
  [ItemTypeConsts.configurationGroup]: ConfigurationGroupApi,
  [ItemTypeConsts.userGroup]: UserGroupApi,
  [ItemTypeConsts.systemGroup]: SystemGroupApi,
};

export class GroupCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();

    mixin(module.actions, ProgressMixin.actions);

    module.actions.fetchEntireCollection = (...params) => this
      .fetchEntireCollection(...params);
    module.actions.fetchActiveDirectoryUserGroups = (...params) => this
      .fetchActiveDirectoryUserGroups(...params);

    module.getters.getGroupsIneligibleForDeletion = (...params) => this
      .getGroupsIneligibleForDeletion(...params);
    module.getters.isManagedByActiveDirectory = (...params) => this
      .isManagedByActiveDirectory(...params);

    module.mutations.ACTIVE_DIRECTORY_USER_GROUPS_HAVE_BEEN_FETCHED = (...params) => this
      .ACTIVE_DIRECTORY_USER_GROUPS_HAVE_BEEN_FETCHED(...params);
    module.mutations.SET_ACTIVE_DIRECTORY_USER_GROUP = (...params) => this
      .SET_ACTIVE_DIRECTORY_USER_GROUP(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = super.getInitialState();
    state.entities.activeDirectoryUserGroups = {};
    state.features = state.features || {};
    state.features.haveActiveDirectoryUserGroupsBeenFetched = false;
    // customer with 351 device groups reported bug of not being able to see
    // newly created device groups they made in the dropdown.
    state.features.items.pagination.limit = 100;
    state.features.items.filters = [
      {
        name: 'type',
        operator: 'ne',
        value: '',
      },
    ];
    return state;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Override action from base class to make a request to DELETE /api/v2/user_groups/{id}
   * and DELETE /api/v2/system_groups/{id} when deleting a User Group or System
   * group, respectively. Unfortunately a request to DELETE /api/v2/groups/{id}
   * doesn't work to generically delete either a System Group or a User Group.
   */
  deleteItems({ commit, dispatch, state }, ids = []) {
    const { boundItems, items } = state.entities;

    // Clone the ids so we don't mutate the original state
    const idsClone = clone(ids);

    return new Promise((resolve) => {
      const deletedItemIds = [];
      const failedTasks = [];
      const tasks = [];

      idsClone.forEach((id) => {
        tasks.push((cb) => {
          const { type } = items.byId[id] || boundItems.byId[id];

          ApiMap[type].delete(id)
            .then(() => {
              deletedItemIds.push(id);
              dispatch('incrementCompleted');
              cb();
            })
            .catch((error) => {
              failedTasks.push({ id, error });
              dispatch('incrementCompleted');
              cb();
            });
        });
      });

      if (tasks.length) {
        dispatch('ProgressModule/updateBulkActionStatus', true, { root: true });
        dispatch('setCounter', { completed: 0, total: tasks.length });

        parallelLimit(tasks, 5, () => {
          dispatch('setCounter', { completed: tasks.length, total: tasks.length });
          commit('DELETE_ITEMS', deletedItemIds);
          dispatch('ProgressModule/updateBulkActionStatus', false, { root: true });
          resolve({ deletedItemIds, failedTasks });
        });
      } else {
        resolve();
      }
    });
  }

  async fetchEntireCollection(context) {
    const { limit } = context.state.features.items.pagination;
    const fetchPromiseArray = [];
    const filters = context.getters.getFilters;

    const { results, totalCount } = await this.Api.fetch(0, limit, 'name', null, null, filters);

    let groupsArray = results;

    for (let skip = limit; skip < totalCount; skip += limit) {
      fetchPromiseArray.push(this.Api.fetch(
        skip, limit, 'name', null, null, filters,
      ));
    }

    const batchArray = await Promise.all(fetchPromiseArray);

    batchArray.forEach((groups) => {
      groupsArray = groupsArray.concat(groups.results);
    });

    context.commit('SET_COLLECTION', {
      results: groupsArray,
      totalCount: groupsArray.length,
      totalCountOfAllItems: totalCount,
    });

    return groupsArray;
  }

  fetchCollection(context, { next = false } = {}) {
    return super
      .fetchCollection(context, { next })
      .then((ids) => {
        if (!context.state.features.haveActiveDirectoryUserGroupsBeenFetched) {
          context.dispatch('fetchActiveDirectoryUserGroups');
        }
        return ids;
      });
  }

  fetchActiveDirectoryUserGroups({ commit }) {
    ActiveDirectoryApi
      .fetchAll()
      .then((activeDirectories) => {
        const tasks = activeDirectories.map(activeDirectory => (cb) => {
          ActiveDirectoryApi
            .fetchAllUserGroups(activeDirectory.id)
            .then(userGroups => cb(null, { activeDirectory, userGroups }))
            .catch(() => cb(null, []));
        });

        parallelLimit(tasks, 5, (err, results) => {
          const userGroupToActiveDirectoryMap = {};

          results.forEach((result) => {
            const { activeDirectory, userGroups } = result;

            userGroups.forEach((userGroup) => {
              const { id } = userGroup;

              if (!userGroupToActiveDirectoryMap[id]) {
                userGroupToActiveDirectoryMap[id] = [];
              }

              userGroupToActiveDirectoryMap[id].push(activeDirectory);
            });
          });

          Object.keys(userGroupToActiveDirectoryMap).forEach(id => commit(
            'SET_ACTIVE_DIRECTORY_USER_GROUP',
            { id, activeDirectories: userGroupToActiveDirectoryMap[id] },
          ));

          commit('ACTIVE_DIRECTORY_USER_GROUPS_HAVE_BEEN_FETCHED');
        });
      });
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  /**
   * Override method from base class to ignore applied filters when determining
   * whether the Groups collection can be refreshed.
   * This is done for the new nav which uses a filter to display User Groups and Device Groups
   * If we need the filter check back we can exclude specific filters
   * by overriding getAppliedFilters instead
   */
  canRefreshCollection(state = { features: { items: {} } }, getters = {}) {
    const { features } = state;
    const { boundItemsUrl, showBoundItems } = features;
    const { searchTerm } = features.items;
    const { getItemsToBind = [], getItemsToUnbind = {} } = getters;
    const hasSelectionChanged = getItemsToBind.length > 0 || getItemsToUnbind.length > 0;

    return !((boundItemsUrl && showBoundItems) || searchTerm || hasSelectionChanged);
  }

  getGroupsIneligibleForDeletion(state, getters) {
    const selectedItemIds = getters.getSelectedItems;

    return selectedItemIds.reduce((acc, id) => {
      if (getters.isManagedByActiveDirectory(id)) {
        acc.adGroups.push(getters.getItem(id));
      }

      return acc;
    }, { adGroups: [] });
  }

  isManagedByActiveDirectory(state) {
    return (id) => {
      const { activeDirectoryUserGroups } = state.entities;
      return !!activeDirectoryUserGroups[id];
    };
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  ACTIVE_DIRECTORY_USER_GROUPS_HAVE_BEEN_FETCHED(state) {
    state.features = state.features || {};
    state.features.haveActiveDirectoryUserGroupsBeenFetched = true;
  }

  RESET_COLLECTION(state, { soft = false, preserveTotalCount = false } = {}) {
    state.features = state.features || {};
    super.RESET_COLLECTION(state, { soft, preserveTotalCount });

    if (soft === false) {
      const initialState = this.getInitialState();

      Vue.set(
        state.features,
        'haveActiveDirectoryUserGroupsBeenFetched',
        initialState.features.haveActiveDirectoryUserGroupsBeenFetched,
      );

      Vue.set(
        state.entities,
        'activeDirectoryUserGroups',
        initialState.entities.activeDirectoryUserGroups,
      );
    }
  }

  SET_ACTIVE_DIRECTORY_USER_GROUP(state, { id, activeDirectories }) {
    Vue.set(
      state.entities.activeDirectoryUserGroups,
      id,
      activeDirectories,
    );
  }
}

export default new GroupCollectionClass(GroupApi, 'id', 'name').extractModule();
